import CommonModal from "@components/common/CommonModal";
import { notify } from "@components/common/Notification";
import { checkFormChanges } from "@lib/checkFormChanges";
import { filterByLabel } from "@lib/filterByLabel";
import {
  DropdownService,
  TenantPermissionService,
  TenantRoleService,
} from "@/services/APIs";
import {
  AssignPermissionPayload,
  UpdatePermissionPayload,
} from "@/utils/types/tenantPermissions";
import { useQuery } from "@tanstack/react-query";
import { Checkbox, Form, Select } from "antd";
import { useEffect, useState } from "react";

interface PermissionModalProps {
  open: boolean;
  onCancel: () => void;
  onSuccess?: () => void;
  mode: "create" | "edit";
  permissionId?: string;
  initialData?: any;
  refetchPermissions: () => void;
  selectedSchema: string;
  selectedRole: string;
}

const PermissionModal = ({
  open,
  onCancel,
  onSuccess,
  mode,
  permissionId,
  initialData,
  refetchPermissions,
  selectedSchema,
  selectedRole,
}: PermissionModalProps) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [form] = Form.useForm();

  const isEditMode = mode === "edit";

  const { data: roleOptions } = useQuery({
    queryKey: ["role"],
    queryFn: () => DropdownService.getDropdownData("TenantRole"),
  });

  const { data: schemaOptions } = useQuery({
    queryKey: ["schema"],
    queryFn: () => DropdownService.getDropdownData("schema"),
  });

  // Fetch permission data for edit mode if not provided
  const { data: permissionData, isLoading } = useQuery({
    queryKey: ["permission", permissionId],
    queryFn: () =>
      TenantPermissionService.getPermissionById(permissionId ?? ""),
    enabled: isEditMode && open && !!permissionId && !initialData,
  });

  const permission = initialData ?? permissionData;

  // Set form values when permission data is available for edit mode
  useEffect(() => {
    if (isEditMode && permission && open) {
      form.setFieldsValue({
        role: permission.role,
        schema_name: permission.schema_name,
        only_if_created: permission.only_if_created ?? false,
        can_select: permission.can_select ?? false,
        can_read: permission.can_read ?? false,
        can_write: permission.can_write ?? false,
        can_create: permission.can_create ?? false,
        can_delete: permission.can_delete ?? false,
        can_print: permission.can_print ?? false,
        can_export: permission.can_export ?? false,
        can_import: permission.can_import ?? false,
        can_email: permission.can_email ?? false,
        can_report: permission.can_report ?? false,
        can_share: permission.can_share ?? false,
        level: permission.level ?? 0,
      });
    } else if (!isEditMode && open) {
      // Reset form for create mode
      form.resetFields();
      const formValues: any = {
        is_active: true, // Default to active for new permissions
        level: 0, // Set default level to 0
      };

      // Set selected schema and role if provided
      if (selectedSchema) {
        formValues.schema_name = selectedSchema;
      }
      if (selectedRole) {
        formValues.role = selectedRole;
      }

      // Set the form values
      form.setFieldsValue(formValues);
    }
  }, [permission, open, form, isEditMode, selectedSchema, selectedRole]);

  const handleSubmit = async () => {
    const values = await form.validateFields();
    setIsSubmitting(true);

    if (isEditMode) {
      // Handle edit mode
      const transformedData: UpdatePermissionPayload = {
        role: values.role,
        schema_name: values.schema_name,
        only_if_created: values.only_if_created ?? false,
        can_select: values.can_select ?? false,
        can_read: values.can_read ?? false,
        can_write: values.can_write ?? false,
        can_create: values.can_create ?? false,
        can_delete: values.can_delete ?? false,
        can_print: values.can_print ?? false,
        can_export: values.can_export ?? false,
        can_import: values.can_import ?? false,
        can_email: values.can_email ?? false,
        can_report: values.can_report ?? false,
        can_share: values.can_share ?? false,
        level: values.level ?? 0,
      };

      // Check for changes before updating
      if (!checkFormChanges(transformedData, permission, notify)) {
        setIsSubmitting(false);
        return;
      }

      await TenantPermissionService.updatePermission(
        permissionId ?? "",
        transformedData
      );
    } else {
      // Handle create mode
      const transformedData: AssignPermissionPayload = {
        role: values.role,
        schema_name: values.schema_name,
        only_if_created: values.only_if_created ?? false,
        can_select: values.can_select ?? false,
        can_read: values.can_read ?? false,
        can_write: values.can_write ?? false,
        can_create: values.can_create ?? false,
        can_delete: values.can_delete ?? false,
        can_print: values.can_print ?? false,
        can_export: values.can_export ?? false,
        can_import: values.can_import ?? false,
        can_email: values.can_email ?? false,
        can_report: values.can_report ?? false,
        can_share: values.can_share ?? false,
        level: values.level ?? 0,
      };

      await TenantRoleService.assignPermissionToRole(
        values.role,
        transformedData
      );
    }

    refetchPermissions();
    onCancel();
    form.resetFields();
    onSuccess?.();
    setIsSubmitting(false);
  };

  const handleCancel = () => {
    onCancel();
    form.resetFields();
  };

  if (isEditMode && isLoading) {
    return (
      <CommonModal
        open={open}
        onCancel={handleCancel}
        title="Edit Permission"
        width={800}
        okText="Update"
        cancelText="Cancel"
        confirmLoading
      >
        <div className="flex h-32 items-center justify-center">
          <div>Loading permission data...</div>
        </div>
      </CommonModal>
    );
  }

  return (
    <CommonModal
      open={open}
      onCancel={handleCancel}
      onOk={handleSubmit}
      title={isEditMode ? "Edit Permission" : "Create Permission"}
      width={800}
      okText={isEditMode ? "Update" : "Create"}
      cancelText="Cancel"
      confirmLoading={isSubmitting}
    >
      <Form form={form} onFinish={handleSubmit}>
        <div className="grid grid-cols-2 gap-4">
          <Form.Item
            name="role"
            label="Role"
            rules={[{ required: true, message: "Please select a role" }]}
          >
            <Select
              disabled={isEditMode}
              options={roleOptions}
              placeholder="Select Role"
              showSearch
              filterOption={filterByLabel}
            />
          </Form.Item>

          <Form.Item
            name="schema_name"
            label="Schema"
            rules={[{ required: true, message: "Please select a schema" }]}
          >
            <Select
              disabled={isEditMode}
              options={schemaOptions?.map((schema: any) => ({
                value: schema.label,
                label: schema.label,
              }))}
              placeholder="Select Schema"
              showSearch
              filterOption={filterByLabel}
            />
          </Form.Item>
        </div>

        <Form.Item
          name="level"
          label="Level"
          rules={[{ required: true, message: "Please enter level" }]}
        >
          <Select
            placeholder="Select Level"
            options={[{ value: 0, label: "Level 0" }]}
          />
        </Form.Item>

        <div className="permission-checkboxes">
          <p>Permissions</p>
          <div>
            <Form.Item label="Read" name="can_read" valuePropName="checked">
              <Checkbox />
            </Form.Item>
            <Form.Item label="Write" name="can_write" valuePropName="checked">
              <Checkbox />
            </Form.Item>
            <Form.Item label="Create" name="can_create" valuePropName="checked">
              <Checkbox />
            </Form.Item>
            <Form.Item label="Delete" name="can_delete" valuePropName="checked">
              <Checkbox />
            </Form.Item>
          </div>

          <p>Additional Permissions</p>
          <div>
            <Form.Item label="Select" name="can_select" valuePropName="checked">
              <Checkbox />
            </Form.Item>
            <Form.Item label="Print" name="can_print" valuePropName="checked">
              <Checkbox />
            </Form.Item>
            <Form.Item label="Export" name="can_export" valuePropName="checked">
              <Checkbox />
            </Form.Item>
            <Form.Item label="Import" name="can_import" valuePropName="checked">
              <Checkbox />
            </Form.Item>
            <Form.Item label="Email" name="can_email" valuePropName="checked">
              <Checkbox />
            </Form.Item>
            <Form.Item label="Report" name="can_report" valuePropName="checked">
              <Checkbox />
            </Form.Item>
            <Form.Item label="Share" name="can_share" valuePropName="checked">
              <Checkbox />
            </Form.Item>
          </div>
        </div>

        <Form.Item
          label="Only If Created"
          name="only_if_created"
          valuePropName="checked"
        >
          <Checkbox />
        </Form.Item>
      </Form>
    </CommonModal>
  );
};

export default PermissionModal;
